import * as yup from 'yup';
import { TAddress } from '@gasbi/shared/common/features/addresses/types';
import { formatSettlementLabel } from '@gasbi/shared/web/forms/AddressForm/SettlementOptionCard';

import {
  AddressFormInitialValues,
  addressFormInitialValues,
  addressFormValidationSchema,
} from '@/forms/AddressForm';

/**
 * The address of a ГРМ card or a структурний підрозділ (GSB-8433): the ЄДРА registry address picked as Supply picks
 * it (the shared AddressForm), its postal code, and the office as a field of its own.
 */
export type CardAddressValues = AddressFormInitialValues & {
  officeNumber: string | null;
};

export const cardAddressInitialValues: CardAddressValues = {
  ...addressFormInitialValues,
  officeNumber: '',
};

/** The fields of the address step: its errors keep the step from moving on, and «Редагувати адресу» checks only them. */
export const cardAddressStepFields = [
  'settlementId',
  'streetId',
  'addressId',
  'postalCode',
  'officeNumber',
];

export const cardAddressValidationSchema = addressFormValidationSchema.shape({
  officeNumber: yup
    .string()
    .trim()
    .max(20, 'Має бути менше або рівне 20 символам')
    .nullable(),
});

/** The fields of the old BMS address, which the card's schema checked and the address form no longer has. */
export const oldAddressFields = [
  'settlement',
  'street',
  'buildingNumber',
  'officeNumber',
] as const;

/** The address form's values for a card, from the registry address it points to (empty for a card not moved yet). */
export const toCardAddressValues = (
  card: {
    addressId?: string | null;
    address?: TAddress | null;
    postalCode?: string | null;
    officeNumber?: string | null;
  },
  formatStreetLabel: (
    streetType: TAddress['streetType'],
    street: string,
  ) => string,
): CardAddressValues => {
  const address = card.address;
  const officeNumber = card.officeNumber ?? '';
  if (!address) return { ...cardAddressInitialValues, officeNumber };

  return {
    ...addressFormInitialValues,
    addressId: card.addressId ?? address.id,
    settlementId: address.settlementId,
    settlement: {
      id: address.settlementId,
      value: formatSettlementLabel(address.settlementType, address.settlement),
      region: address.region,
      district: address.district,
      community: address.community,
    },
    regionId: address.region ?? null,
    region: address.region
      ? { id: address.region, value: address.region }
      : null,
    districtId: address.district ?? null,
    district: address.district
      ? { id: address.district, value: address.district }
      : null,
    communityId: address.community ?? null,
    community: address.community
      ? { id: address.community, value: address.community }
      : null,
    streetId: address.streetId,
    street: {
      id: address.streetId,
      value: formatStreetLabel(address.streetType, address.street),
    },
    building: {
      id: address.id,
      value: address.buildingNumber,
      postalCode: address.postalCode,
    },
    postalCode: card.postalCode ?? null,
    officeNumber,
  };
};

/**
 * The request of a card with its address: the registry id, the postal code and the office. The form's pickers and the
 * old BMS address fields are not sent.
 */
const notSentFields = [
  'address',
  'settlement',
  'region',
  'district',
  'community',
  'street',
  'building',
];

export const toCardAddressRequest = <T extends object>(values: T) => {
  const card: Record<string, unknown> = Object.fromEntries(
    Object.entries(values).filter(([key]) => !notSentFields.includes(key)),
  );
  const text = (value: unknown) =>
    typeof value === 'string' && value.trim() ? value.trim() : null;

  return {
    ...card,
    addressId: card.addressId ?? null,
    postalCode: text(card.postalCode),
    officeNumber: text(card.officeNumber),
    regionId: null,
    settlementId: null,
    streetName: null,
    buildingNumber: null,
  };
};
